// Standalone, unlinked client-validation page at /narrative-test.
//
// Repurposes the Timeline view's layout — top toolbar, bottom scrubber,
// right-hand narrative panel — to walk Homebase's engineering history as a
// 10-snapshot narrative arc. The center reuses the REAL platform-map matrix
// (PlatformMapMatrix), fed by Homebase's live per-date timeline snapshot, so
// each era shows the genuine derivedContext × business-function map at that point
// in time. A left pull-out drawer — mirroring the nav rail's collapse/expand
// chrome — houses the Risks and What's-already-being-done analysis.

import type { Skill } from '@lib/api-types/derived-contexts';
import type { TimelineSnapshot } from '@lib/api-types/views';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fetchJson } from '../../api.ts';
import {
  FloatingToolbar,
  ShowLibrariesIcon,
  ToolbarSelect,
  ToolbarSeparator,
  ToolbarToggle,
} from '../../snapshot/components/FloatingToolbar.tsx';
import { ChevronLeftIcon, ScaleIcon } from '../../snapshot/components/Icons.tsx';
import { PlatformMapMatrix } from '../../snapshot/components/PlatformMapMatrix.tsx';
import { TimelineScrubber } from '../../snapshot/components/TimelineScrubber.tsx';
import { TooltipProvider } from '../../snapshot/components/Tooltip.tsx';

import { ERAS, PROGRESS_ITEMS, RISK_GROUPS, SNAPSHOTS, eraById } from './narrative-data.ts';
import './narrative-test.css';

// This page is Homebase-specific, so it pins the org slug rather than reading
// the viewer's active org — it renders the same story regardless of context.
const HOMEBASE_SLUG = 'homebase';

/**
 * Homebase's real platform-map snapshot at an ISO date. Hits the same
 * per-date timeline endpoint the live Timeline uses — the compose is
 * memory-safe per date, so org 32's size is fine. `null` date disables it.
 */
function useHomebaseSnapshot(at: string | null) {
  return useQuery<TimelineSnapshot | null, Error>({
    enabled: at !== null,
    queryKey: ['narrative', 'homebase', 'snapshot', at],
    queryFn: () =>
      fetchJson<{ snapshot: TimelineSnapshot | null }>(
        `/v1/orgs/${HOMEBASE_SLUG}/views/timeline/snapshot?at=${encodeURIComponent(at!)}`,
      ).then((r) => r.snapshot),
    staleTime: 5 * 60_000,
    placeholderData: (prev) => prev,
  });
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

// "Apr-25-2013" — parse parts directly so a UTC date doesn't shift a day.
function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return `${MONTHS[Number(m[2]) - 1]}-${m[3]}-${m[1]}`;
}

// "Apr 25, 2013" — long form for the snapshot caption.
function formatLongDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  return `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** Left pull-out drawer — mirrors the nav rail's collapse/expand chrome. */
function AnalysisRail({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  return (
    <aside className={`nt-rail${open ? ' is-open' : ''}`} aria-label='Risks & progress'>
      <button
        type='button'
        className='nt-rail__toggle'
        onClick={onToggle}
        aria-expanded={open}
        aria-label={open ? 'Collapse analysis' : 'Expand analysis'}
      >
        {open ? <ChevronLeftIcon /> : <ScaleIcon />}
        <span className='nt-rail__toggle-label'>{open ? 'Close' : ''}</span>
      </button>

      {open ? null : (
        <button type='button' className='nt-rail__tab' onClick={onToggle}>
          Risks &amp; what’s being done
        </button>
      )}

      <div className='nt-rail__body'>
        <h3 className='nt-rail__h'>Risks to surface today</h3>
        {RISK_GROUPS.map((g) => (
          <div key={g.heading} className='nt-rail__group'>
            <div className='nt-rail__group-h nt-rail__group-h--risk'>{g.heading}</div>
            <ul className='nt-rail__list'>
              {g.items.map((it, i) => (
                <li key={i} className='nt-rail__item nt-rail__item--risk'>
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}

        <h3 className='nt-rail__h nt-rail__h--progress'>What’s already being done</h3>
        <ul className='nt-rail__list'>
          {PROGRESS_ITEMS.map((it, i) => (
            <li key={i} className='nt-rail__item nt-rail__item--progress'>
              {it}
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export function NarrativeTest() {
  const [index, setIndex] = useState(0);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [showLibraries, setShowLibraries] = useState(false);

  const snap = SNAPSHOTS[Math.min(index, SNAPSHOTS.length - 1)]!;
  const era = eraById(snap.eraId);

  const { data: liveSnapshot, isLoading, isError } = useHomebaseSnapshot(snap.date);
  // Warm the next era's snapshot so forward scrubbing / autoplay is instant.
  const nextDate = SNAPSHOTS[index + 1]?.date ?? null;
  useHomebaseSnapshot(nextDate);

  // "Show Libraries" off hides library-kind skills, matching the Timeline.
  const techVisible = useMemo<(s: Skill) => boolean>(
    () => (showLibraries ? () => true : (s) => s.kind !== 'library'),
    [showLibraries],
  );

  const eraOptions = useMemo(
    () => ERAS.map((e) => ({ value: e.id, label: `${e.chapter} · ${e.title}` })),
    [],
  );
  const jumpToEra = (eraId: string) => {
    const i = SNAPSHOTS.findIndex((s) => s.eraId === eraId);
    if (i >= 0) setIndex(i);
  };

  const hasMatrix = !!liveSnapshot && liveSnapshot.derivedContexts.length > 0;

  return (
    <div className={`nt-root${analysisOpen ? ' nt-root--drawer-open' : ''}`}>
      <AnalysisRail open={analysisOpen} onToggle={() => setAnalysisOpen((o) => !o)} />

      <section className='nt-page'>
        <div className='nt-eyebrow'>Homebase · pioneerworks · engineering history</div>
        <h1 className='snap-h1 nt-h1'>How Homebase’s codebase came to be</h1>
        <p className='snap-subtitle nt-subtitle'>
          The platform map — derivedContexts × business functions — snapshotted through
          13 years of history. Drag the scrubber, hit play for a guided tour, or jump
          to a chapter.
        </p>

        <FloatingToolbar>
          <ToolbarSelect
            label='Jump to chapter'
            value={era.id}
            options={eraOptions}
            onChange={jumpToEra}
          />
          <ToolbarSeparator />
          <ToolbarToggle
            label='Show Libraries'
            pressed={showLibraries}
            onToggle={() => setShowLibraries((v) => !v)}
          >
            <ShowLibrariesIcon />
          </ToolbarToggle>
        </FloatingToolbar>

        <div className='snap-timeline nt-timeline'>
          <div className='snap-timeline__content'>
            <div className='nt-caption'>
              <span className='nt-caption__label'>Platform snapshot</span>
              <strong className='nt-caption__date'>{formatLongDate(snap.date)}</strong>
              <span className='nt-caption__moment'>· {snap.cardTitle}</span>
            </div>
            <div className='nt-matrix-scroll'>
              {isLoading ? (
                <div className='nt-matrix-msg'>Composing the platform map for {formatLongDate(snap.date)}…</div>
              ) : isError ? (
                <div className='nt-matrix-msg'>
                  Couldn’t load this snapshot. Make sure you’re signed in with access to Homebase.
                </div>
              ) : hasMatrix ? (
                <TooltipProvider>
                  <PlatformMapMatrix
                    snapshot={liveSnapshot}
                    techVisible={techVisible}
                  />
                </TooltipProvider>
              ) : (
                <div className='nt-matrix-msg'>
                  No platform-map coverage recorded at this date — the derived catalog may not
                  reach this far back yet.
                </div>
              )}
            </div>
          </div>

          <aside className='snap-snapshot-notes nt-notes'>
            <div className='nt-notes__chapter'>
              {era.chapter} · {era.range}
            </div>
            <div className='snap-snapshot-notes__date nt-notes__title'>{era.title}</div>
            <p className='snap-snapshot-notes__desc nt-notes__desc'>{era.copy}</p>
          </aside>
        </div>

        <TimelineScrubber
          snapshots={SNAPSHOTS}
          index={index}
          onIndexChange={setIndex}
          formatDate={formatDate}
        />
      </section>
    </div>
  );
}
